/**
 * Feed Card Transformer
 * Transforms MobileProcessedArticleData FeedCard format
 */

import {
  type CardComponent,
  type CardSize,
  type MobileApiPath,
} from "@mobile-constants/MobileFeedConstants";
import type {
  ArticleData,
  displaySizeData,
  FeedAuthor,
  FeedCard,
  FeedImage,
  FeedItem,
  FeedSection,
  FeedTag,
  LayoutDetails,
  MobileProcessedArticleData,
} from "@mobile-types/MobileTypes";
import type {
  CueArticleSection,
  CueArticleSectionParent,
} from "@typings/Opensearch";

// ============================================
// TYPES
// ============================================

/**
 * Card configuration for building FeedCards
 */
export interface CardConfig {
  cardSizes: {
    compact: CardSize | "";
  };
  cardComponents?: CardComponent[];
  displaySize?: displaySizeData;
  headerComponent?: LayoutDetails["headerComponent"];
}

// ============================================
// CARD BUILDER FUNCTIONS
// ============================================

/**
 * Builds a FeedCard from feed items and card configuration
 */
export function buildFeedCard(
  feedItems: FeedItem[],
  card: CardConfig,
): FeedCard {
  const feedCard: FeedCard = {
    layoutDetails: {
      cardSizes: card.cardSizes,
      ...(card.cardComponents && { cardComponents: card.cardComponents }),
      ...(card.displaySize && { displaySize: card.displaySize }),
      ...(card.headerComponent && { headerComponent: card.headerComponent }),
    },
    items: feedItems,
  };

  return feedCard;
}

// ============================================
// ARTICLE DATA TRANSFORMATION
// ============================================

/**
 * Transforms MobileProcessedArticleData to ArticleData (BT format)
 */
export function transformToArticleData(
  article: MobileProcessedArticleData,
): ArticleData {
  return {
    id: article.id,
    title: transformTitle(article.title),
    urlPath: article.urlPath || article.linkTo,
    openInWebView: false,
    publishTime: article.dateCreated,
    editedTime: article.dateEdited || article.dateCreated,
    kicker: article.kicker || "",
    paidMode: getPaidMode(article.paidMode),
    copyright: "",
    blurb: article.standFirst || article.paragraph || "",
    tags: transformTags(article.tags),
    sections: transformSections(article.sections),
    authors: transformAuthors(article.authors),
    externalUrl: "",
    articleType: article.articleType,
    hasUpdatedTimeDisplayed: article.hasUpdatedTimeDisplayed,
    images: transformImages(article),
  };
}

// ============================================
// FIELD TRANSFORMERS
// ============================================

/**
 * Transforms title - removes newlines
 */
function transformTitle(title: string = ""): string {
  return title.replace(/\n/g, " ").trim();
}

/**
 * Gets paid mode from string
 */
function getPaidMode(paidMode?: string): "free" | "premium" {
  return paidMode === "premium" || paidMode === "1" ? "premium" : "free";
}

/**
 * Appends `_plat/api` prefix while preserving literal type.
 */
export const pathPrefix = <T extends MobileApiPath>(
  path: T,
): `_plat/api${T}` => {
  return `_plat/api${path}`;
};

/**
 * Transforms tags to FeedTag format
 */
export function transformTags(
  tags: MobileProcessedArticleData["tags"],
): FeedTag[] {
  return tags.map((tag) => ({
    id: tag.id || 0,
    name: tag.name || "",
    type: "keyword" as const,
    urlPath: tag.alias || "",
    urlPathHistory: [],
    sections: [],
  }));
}

/**
 * Transforms sections to FeedSection format
 */
export function transformSections(
  sections: MobileProcessedArticleData["sections"],
): FeedSection[] {
  if (!sections || !Array.isArray(sections)) {
    return [];
  }

  return sections.map((section, index) => ({
    name: section.name || "",
    uniqueName: section.uniqueName || "",
    level: index === 0 ? 1 : 2,
    urlPath: `/${section.uniqueName || ""}`,
  }));
}

type AncestorNode = { code: string; name: string };

const buildAncestorChain = (
  parent: CueArticleSection["parent"],
): Array<AncestorNode & { level: number }> => {
  const chain: AncestorNode[] = [];
  let node: CueArticleSectionParent | null | undefined = parent;
  while (node) {
    const { name, uniqueName } = node;
    const next: CueArticleSectionParent | null | undefined = node.parent;
    if (name && name !== "Home" && uniqueName) {
      chain.unshift({ code: uniqueName, name });
    }
    node = next;
  }
  return chain.map((item, i) => ({ ...item, level: i + 1 }));
};

/**
 * Transforms sections into article_categories format matching bt-backend-api structure.
 * Walks the full parent chain to support 1-, 2-, and 3-level hierarchies.
 * "Home" nodes at any level are skipped. Levels are assigned top-down (1 = topmost ancestor).
 */
export function transformCategories(
  sections: CueArticleSection[] | undefined,
): string {
  if (!sections || !Array.isArray(sections)) return JSON.stringify([]);

  return JSON.stringify(
    sections.flatMap(({ name, uniqueName, parent }) => {
      if (!uniqueName || name === "Home") return [];

      const ancestors = buildAncestorChain(parent);

      return [
        {
          sections: [
            ...ancestors,
            { code: uniqueName, name, level: ancestors.length + 1 },
          ],
        },
      ];
    }),
  );
}

/**
 * Transforms authors to FeedAuthor format
 */
export function transformAuthors(
  authors: MobileProcessedArticleData["authors"],
): FeedAuthor[] {
  return authors.map((author) => ({
    id: author.id || 0,
    type: author.hasProfile ? "authorProfile" : "",
    name: typeof author.name === "string" ? author.name : "",
    image: author.photo || author.authorImage?.url || "",
    urlPath: author.urlPath || "",
  }));
}

/**
 * Transforms images to FeedImage format with multiple crops
 */
export function transformImages(
  article: MobileProcessedArticleData,
): FeedImage[] {
  const { imageGallery, imageOriginalGallery, imageCropsGallery } = article;

  if (imageGallery.length === 0) {
    return getDefaultImage();
  }

  return imageGallery.flatMap((img, index) => {
    const originalImg = imageOriginalGallery?.[index];
    const cropItem = imageCropsGallery[index];
    const imageUrl = img.image?.url || "";

    if (!imageUrl) {
      return getDefaultImage();
    }

    const originalUrl = originalImg?.image?.url || imageUrl;

    return {
      id: parseInt(img.id) || index,
      type: "picture" as const,
      caption: img.caption || "",
      credit: img.credit || "",
      original: {
        url: originalUrl,
        width: originalImg?.image?.width || img.image?.width || 0,
        height: originalImg?.image?.height || img.image?.height || 0,
      },
      landscape: {
        url: cropItem.landscape?.url || imageUrl,
        width: cropItem.landscape?.width || img.image?.width || 0,
        height: cropItem.landscape?.height || img.image?.height || 0,
      },
      portrait: {
        url: cropItem.portrait?.url || imageUrl,
        width: cropItem.portrait?.width || img.image?.width || 0,
        height: cropItem.portrait?.height || img.image?.height || 0,
      },
      square: {
        url: cropItem.square?.url || imageUrl,
        width: cropItem.square?.width || img.image?.width || 0,
        height: cropItem.square?.height || img.image?.height || 0,
      },
    };
  });
}

/**
 * Returns default image when no images available
 */
function getDefaultImage(): FeedImage[] {
  const defaultImageUrl =
    "https://www.tamilmurasu.com.sg/web2-assets/tm_default_image.png";

  return [
    {
      id: 0,
      type: "picture",
      caption: "",
      credit: "",
      original: { url: defaultImageUrl, width: 640, height: 428 },
      landscape: { url: defaultImageUrl, width: 640, height: 428 },
      portrait: { url: defaultImageUrl, width: 640, height: 428 },
      square: { url: defaultImageUrl, width: 640, height: 428 },
    },
  ];
}
